import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ClassRequestWithStudent, ClassRequestData } from '@/types/classRequests';
import { useToast } from '@/components/ui/use-toast';

// Hook to get instructor pending requests count
export const useInstructorPendingRequestsCount = (instructorId: string) => {
  return useQuery({
    queryKey: ['instructor-pending-requests-count', instructorId],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_instructor_pending_requests_count', {
          instructor_id: instructorId
        });

      if (error) throw error;
      return data || 0;
    },
    enabled: !!instructorId,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

// Hook to get instructor requests with student info
export const useInstructorRequests = (instructorId: string) => {
  return useQuery({
    queryKey: ['instructor-requests', instructorId],
    queryFn: async (): Promise<ClassRequestWithStudent[]> => {
      const { data, error } = await supabase
        .rpc('get_instructor_requests_with_students', {
          instructor_id: instructorId
        });

      if (error) throw error;
      
      // Don't filter out past requests - we need them to filter slots
      // Only filter out very old requests (more than 7 days old)
      const now = new Date();
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const sevenDaysAgoStr = sevenDaysAgo.toISOString().split('T')[0];
      
      const filteredData = (data || []).filter(request => {
        // Only show pending requests and filter out very old requests
        return request.status === 'pending' && request.requested_date >= sevenDaysAgoStr;
      });
      
      
      return filteredData;
    },
    enabled: !!instructorId,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

// Hook to submit class request
export const useClassRequestSubmission = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (requestData: ClassRequestData) => {
      const { data, error } = await supabase
        .rpc('create_class_request', {
          p_instructor_id: requestData.instructor_id,
          p_student_id: requestData.student_id,
          p_requested_date: requestData.requested_date,
          p_requested_start_time: requestData.requested_start_time,
          p_requested_end_time: requestData.requested_end_time,
          p_message: requestData.message || null
        });

      if (error) throw error;
      return data;
    },
    onSuccess: (requestId, variables) => {
      toast({
        title: 'Request Submitted',
        description: 'Your class request has been submitted successfully.',
      });
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['instructor-pending-requests-count'] });
      queryClient.invalidateQueries({ queryKey: ['instructor-requests'] });
      queryClient.invalidateQueries({ queryKey: ['student-requests', variables.student_id] });
    },
    onError: (error: any) => {
      // Handle duplicate request error specifically
      if (error.code === '23505' && error.message.includes('duplicate key value violates unique constraint')) {
        toast({
          title: 'Request Already Exists',
          description: 'You have already submitted a request for this time slot.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Error',
          description: error.message || 'Failed to submit request. Please try again.',
          variant: 'destructive',
        });
      }
    },
  });
};

// Hook to approve class request
export const useApproveClassRequest = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ requestId, instructorId }: { requestId: string; instructorId: string }) => {
      // Get the specific request directly from the table (check any status first)
      const { data: requestData, error: requestError } = await supabase
        .from('class_scheduling_requests')
        .select('*')
        .eq('id', requestId)
        .eq('instructor_id', instructorId)
        .single();
      
      if (requestError) {
        console.error('❌ Error fetching request:', requestError);
        throw new Error('Request not found');
      }

      if (!requestData) {
        throw new Error('Request not found');
      }

      if (requestData.status !== 'pending') {
        throw new Error(`Request is already ${requestData.status}`);
      }


      // Update request status - use the actual id from the database
      const { error: updateError } = await supabase
        .from('class_scheduling_requests')
        .update({ status: 'approved', updated_at: new Date().toISOString() })
        .eq('id', requestData.id); // Use the actual id from the table

      if (updateError) throw updateError;

      // Create training for student with correct timezone
      const startDateTime = new Date(`${requestData.requested_date}T${requestData.requested_start_time}`);
      const endDateTime = new Date(`${requestData.requested_date}T${requestData.requested_end_time}`);
      
      const { data: trainingData, error: trainingError } = await supabase
        .from('trainings')
        .insert({
          title: 'Private Class',
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          modality: 'Gi', // Use valid modality value
          status: 'Scheduled',
          type: 'Class',
          category: 'Private Class',
          description: 'Private class session',
          notes: requestData.message,
          duration: 60, // Default 60 minutes
          academy_id: requestData.academy_id,
          user_id: requestData.student_id,
          is_class: true
        })
        .select()
        .single();

      if (trainingError) throw trainingError;

      // Create training_instructors
      const { error: instructorError } = await supabase
        .from('training_instructors')
        .insert({
          training_id: trainingData.id,
          instructor_user_id: instructorId
        });

      if (instructorError) throw instructorError;

      // Create class for instructor with correct timezone
      console.log('🔍 Creating class for instructor:', { instructorId, startDateTime: startDateTime.toISOString(), endDateTime: endDateTime.toISOString() });
      
      const { data: classData, error: classError } = await supabase
        .from('trainings')
        .insert({
          title: 'Private Class',
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString(),
          modality: 'Gi',
          status: 'Scheduled',
          type: 'Class',
          category: 'Private Class',
          description: 'Private class session',
          notes: requestData.message,
          duration: 60,
          academy_id: requestData.academy_id,
          user_id: instructorId, // Instructor as the user
          is_class: true
        })
        .select()
        .single();

      console.log('🔍 Class creation result:', { classData, classError });

      if (classError) throw classError;

      // Create training_instructors for the class (instructor teaching)
      console.log('🔍 Creating training_instructors for class:', { trainingId: classData.id, instructorId });
      
      const { error: classInstructorError } = await supabase
        .from('training_instructors')
        .insert({
          training_id: classData.id,
          instructor_user_id: instructorId
        });

      console.log('🔍 Training_instructors creation result:', { classInstructorError });

      if (classInstructorError) throw classInstructorError;

      // Create training_partners for the class (student as partner)
      console.log('🔍 Creating training_partners for class:', { trainingId: classData.id, studentId: requestData.student_id });
      
      const { error: classPartnerError } = await supabase
        .from('training_partners')
        .insert({
          training_id: classData.id,
          partner_user_id: requestData.student_id
        });

      console.log('🔍 Training_partners creation result:', { classPartnerError });

      if (classPartnerError) throw classPartnerError;

      // Create notification
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert({
          user_id: requestData.student_id,
          type: 'class_approved',
          title: 'Class Request Approved',
          content: 'Your private class request has been approved and scheduled.',
          metadata: {
            request_id: requestData.id,
            training_id: trainingData.id,
            instructor_id: instructorId
          }
        });

      if (notificationError) throw notificationError;

      return { success: true, training_id: trainingData.id };
    },
    onSuccess: () => {
      toast({
        title: 'Request Approved',
        description: 'Class request has been approved and scheduled.',
      });
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['instructor-pending-requests-count'] });
      queryClient.invalidateQueries({ queryKey: ['instructor-requests'] });
      queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
      queryClient.invalidateQueries({ queryKey: ['trainings'] });
      queryClient.invalidateQueries({ queryKey: ['next-trainings'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to approve request. Please try again.',
        variant: 'destructive',
      });
    },
  });
};

// Hook to deny class request
export const useDenyClassRequest = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ requestId, instructorId, reason }: { requestId: string; instructorId: string; reason?: string }) => {
      const { data, error } = await supabase
        .rpc('deny_class_request', {
          p_request_id: requestId,
          p_instructor_id: instructorId,
          p_reason: reason || null
        });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: 'Request Denied',
        description: 'Class request has been denied.',
      });
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['instructor-pending-requests-count'] });
      queryClient.invalidateQueries({ queryKey: ['instructor-requests'] });
      queryClient.invalidateQueries({ queryKey: ['next-trainings'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to deny request. Please try again.',
        variant: 'destructive',
      });
    },
  });
};

// Hook to get student's own requests
export const useStudentRequests = (studentId: string) => {
  return useQuery({
    queryKey: ['student-requests', studentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('class_scheduling_requests')
        .select(`
          *,
          instructor:profiles!instructor_id(full_name, profile_picture_url)
        `)
        .eq('student_id', studentId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!studentId,
  });
};
